import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "@/lib/firebase";
import {
  Calendar,
  CalendarPlus,
  BookOpen,
  Mail,
  LogOut,
  Settings,
  Menu,
  X,
  Users,
  BarChart2,
  Database,
  ReceiptText,
  ChevronLeft,
  ChevronRight,
  Activity,
  Bluetooth,
  BluetoothOff,
  Store,
  Check,
  ChevronsUpDown,
  CreditCard,
  PieChart,
  Plus,
  MailCheck,
  ClipboardList,
  CalendarClock
} from "lucide-react";
import { LoadingIndicator as Loader2 } from "@/components/ui/loading-indicator";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useBluetooth } from "@/contexts/BluetoothContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { getSiteBrand, groupSitesByBrand } from "@/lib/site-brands";
import { getAdminByEmail } from "@/lib/firestore";
import { useSitesData } from "@/contexts/DataContext";
import type { AdminRole } from "@/types";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { preloadAdminRoute } from "@/lib/route-loaders";
import { useIsMobile } from "@/hooks/use-mobile";

const QualiReparDialog = lazy(() => import("./QualiReparDialog").then(m => ({ default: m.QualiReparDialog })));

const preloadLinkProps = (href?: string) => ({
  onMouseEnter: () => preloadAdminRoute(href),
  onFocus: () => preloadAdminRoute(href),
  onPointerDown: () => preloadAdminRoute(href),
});

// Retour au toucher : léger enfoncement (skill Emil Kowalski, « buttons must feel responsive »).
const pressable = "transition-[color,background-color,box-shadow,transform] duration-150 ease-out active:scale-[0.97] motion-reduce:active:scale-100";
// Ombre semi-transparente plutôt qu'une bordure pleine.
const surface = "bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]";
const primaryButton = "bg-blue-600 text-white shadow-[0_1px_2px_rgba(37,99,235,0.35),inset_0_1px_0_rgba(255,255,255,0.15)] hover:bg-blue-700 active:bg-blue-800";
const tooltipClassName = "rounded-lg border-none bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg";

const navGroups = [
  {
    title: "PRODUCTION",
    items: [
      { label: "Création dossier", action: "create-claim", icon: CalendarPlus, roles: ["patron", "technicien"] },
      { label: "Réservations", href: "/dashboard/bookings", icon: BookOpen, roles: ["patron", "technicien"] },
      { label: "Détails opérations", href: "/dashboard/operations", icon: ClipboardList, roles: ["patron", "comptabilite"] },
      { label: "Opérations à venir", href: "/dashboard/upcoming-operations", icon: CalendarClock, roles: ["patron", "comptabilite"] },
      { label: "Campagnes (E-mail & SMS)", href: "/dashboard/campaigns", icon: Mail, roles: ["patron"], activeFor: ["/dashboard/emails"] },
      // Accessible depuis la page Campagnes ; gardé ici pour le titre mobile et les droits d'accès.
      { label: "Modèles d'e-mails", href: "/dashboard/emails", icon: MailCheck, roles: ["patron"], hidden: true },
    ]
  },
  {
    title: "ORGANISATION",
    items: [
      { label: "Types d'événements", href: "/dashboard/events", icon: Calendar, roles: ["patron"], activeFor: ["/dashboard/sites", "/dashboard/points-of-sale"] },
      // Accessibles depuis l'engrenage de Types d'événements ; gardés ici pour le titre mobile et les droits d'accès.
      { label: "Sites", href: "/dashboard/sites", icon: Store, roles: ["patron"], hidden: true },
      { label: "Points de vente", href: "/dashboard/points-of-sale", icon: CreditCard, roles: ["patron"], hidden: true },
    ]
  },
  {
    title: "GESTION & ANALYSE",
    items: [
      { label: "Tableau de Bord", href: "/dashboard/analytics", icon: BarChart2, roles: ["patron"] },
      { label: "Statistiques", href: "/dashboard/statistics", icon: PieChart, roles: ["patron"] },
      { label: "Factures & Ventes", href: "/dashboard/billing", icon: CreditCard, roles: ["patron", "comptabilite"] },
      { label: "Synthèse financière", href: "/dashboard/transactions", icon: ReceiptText, roles: ["patron", "comptabilite"] },
    ]
  },
  {
    title: "PARAMÈTRES",
    items: [
      { label: "Clients", href: "/dashboard/clients", icon: Users, roles: ["patron", "comptabilite"] },
      { label: "API Agora", href: "/dashboard/api-tester", icon: Database, roles: ["patron"] },
      { label: "Statut système", href: "/dashboard/status", icon: Activity, roles: ["patron"] },
      { label: "Configuration", href: "/dashboard/settings", icon: Settings, roles: ["patron"] },
    ]
  }
];

// Recherche insensible aux accents et à la casse (« depot » trouve « Electro Dépôt »).
const normalizeSearch = (text: string) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const filterSites = (_value: string, search: string, keywords?: string[]) =>
  normalizeSearch((keywords ?? []).join(" ")).includes(normalizeSearch(search.trim())) ? 1 : 0;

const isNavItemActive = (item: { href?: string; activeFor?: string[] }, pathname: string) => Boolean(item.href && (
  pathname === item.href
  || (item.href !== "/dashboard" && pathname.startsWith(item.href))
  || item.activeFor?.some((path) => pathname.startsWith(path))
));

type SiteSummary = {
  id: string;
  name: string;
};

export default function AdminLayout() {

  const location = useLocation();
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<AdminRole | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const sidebarCollapsed = isSidebarCollapsed && !isMobile;
  const { isBluetoothConnected, isConnecting, connectBluetooth } = useBluetooth();
  const [isQualiReparDialogOpen, setIsQualiReparDialogOpen] = useState(false);
  const [isSitePickerOpen, setIsSitePickerOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState("");
  const [openSiteBrands, setOpenSiteBrands] = useState<string[]>([]);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    PRODUCTION: true,
    ORGANISATION: false,
    "GESTION & ANALYSE": true,
    "PARAMÈTRES": false
  });

  const toggleGroup = (title: string) => {
    setOpenGroups(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const { sites } = useSitesData();
  const [activeSiteId, setActiveSiteId] = useState<string | null>(localStorage.getItem("active_site_id"));

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        sessionStorage.removeItem("cached_admin_user");
        navigate("/login", { replace: true });
        setAuthLoading(false);
      } else {
        setUserEmail(user.email);
        // Finalisations QualiRépar interrompues sur cet appareil (coupure, page rechargée).
        void import("@/lib/qualirepar-submission")
          .then(({ resumeStoredQualiReparJobs }) => resumeStoredQualiReparJobs())
          .catch((error) => console.error("[QualiRépar] Reprise des dossiers sauvegardés impossible:", error));
        
        // Performance optimization: Check session cache first
        const cachedAdmin = sessionStorage.getItem("cached_admin_user");
        if (cachedAdmin) {
          try {
            const parsed = JSON.parse(cachedAdmin);
            if (parsed.email === user.email) {
              setUserRole(parsed.role);
              setAuthLoading(false);
              return;
            }
          } catch (e) {
            sessionStorage.removeItem("cached_admin_user");
          }
        }

        if (user.email) {
          try {
            const adminData = await getAdminByEmail(user.email);
            if (adminData) {
              setUserRole(adminData.role);
              // Cache for faster subsequent loads
              sessionStorage.setItem("cached_admin_user", JSON.stringify({
                email: user.email,
                role: adminData.role
              }));
            } else {
              // Explicitly set null or a "guest" role instead of technican to avoid UI flicker
              setUserRole(null);
            }
          } catch (e) {
            console.error("Error fetching admin role:", e);
            setUserRole(null);
          }
        }
        setAuthLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    if (!activeSiteId && sites.length > 0) {
      handleSelectSite(sites[0]);
    }
  }, [sites, activeSiteId]);

  const handleSelectSite = (site: SiteSummary) => {
    setActiveSiteId(site.id);
    localStorage.setItem("active_site_id", site.id);
    localStorage.setItem("active_site_name", site.name);
    toast.success(`Site actif : ${site.name}`);
  };

  // À chaque ouverture : recherche vide, seule l'enseigne du site actuel est dépliée.
  const handleSitePickerOpenChange = (open: boolean) => {
    if (open) {
      setSiteSearch("");
      const activeName = sites.find((site) => site.id === activeSiteId)?.name;
      setOpenSiteBrands(activeName ? [getSiteBrand(activeName).brand] : []);
    }
    setIsSitePickerOpen(open);
  };

  const toggleSiteBrand = (brand: string) => {
    setOpenSiteBrands((current) => current.includes(brand)
      ? current.filter((openBrand) => openBrand !== brand)
      : [...current, brand]);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login", { replace: true });
  };

  const initials = userEmail ? userEmail.substring(0, 2).toUpperCase() : "LT";
  const canCreateClaim = Boolean(userRole && navGroups.some((group) => (
    group.items.some((item) => item.action === "create-claim" && item.roles.includes(userRole))
  )));
  const activeSite = sites.find(s => s.id === activeSiteId);
  const siteGroups = useMemo(() => groupSitesByBrand<SiteSummary & { logoUrl?: string }>(sites), [sites]);
  const currentNavItem = navGroups
    .flatMap((group) => group.items)
    .filter((item) => item.href && location.pathname.startsWith(item.href))
    .sort((first, second) => (second.href?.length || 0) - (first.href?.length || 0))[0];
  const currentPageTitle = currentNavItem?.label || "Tableau de bord";

  useEffect(() => {
    if (!authLoading && userRole === "technicien") {
      const allItems = navGroups.flatMap(g => g.items);
      const currentNavItem = allItems.find(item => item.href && location.pathname.startsWith(item.href));
      if (currentNavItem && !currentNavItem.roles.includes("technicien")) {
        navigate("/dashboard/analytics", { replace: true });
        toast.error("Accès restreint aux patrons.");
      }
    }
  }, [location.pathname, userRole, authLoading, navigate]);

  useEffect(() => {
    setIsMobileMenuOpen(false);

    const activeGroup = navGroups.find((group) => (
      group.items.some((item) => isNavItemActive(item, location.pathname))
    ));
    if (activeGroup) {
      setOpenGroups((current) => current[activeGroup.title]
        ? current
        : { ...current, [activeGroup.title]: true });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMobileMenuOpen(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isMobileMenuOpen]);

  if (authLoading) {
    return (
      <div className="flex h-dvh items-center justify-center bg-[#f8f9fa]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const printerStatus = isConnecting ? "Connexion…" : isBluetoothConnected ? "Connectée" : "Déconnectée";
  const printerIcon = isConnecting
    ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
    : isBluetoothConnected
      ? <Bluetooth className="h-4 w-4 shrink-0" />
      : <BluetoothOff className="h-4 w-4 shrink-0" />;
  const siteLogo = (size: "sm" | "md") => (
    <div className={cn(
      "flex shrink-0 items-center justify-center overflow-hidden",
      size === "md" ? "h-9 w-9 rounded-lg" : "h-8 w-8 rounded-md",
      activeSite?.logoUrl ? "bg-white ring-1 ring-black/5" : "bg-blue-600 text-white"
    )}>
      {activeSite?.logoUrl ? (
        <img src={activeSite.logoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <Store className="h-4 w-4" />
      )}
    </div>
  );

  return (
    <div className="flex h-dvh bg-[#f8f9fa] overflow-hidden">
      {/* Toujours monté pour que le voile apparaisse et disparaisse avec le tiroir. */}
      <button
        type="button"
        aria-label="Fermer le menu"
        aria-hidden={!isMobileMenuOpen}
        tabIndex={isMobileMenuOpen ? 0 : -1}
        className={cn(
          "fixed inset-0 z-20 bg-black/40 transition-opacity duration-300 ease-drawer md:hidden",
          isMobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      <aside className={cn(
        "fixed md:static inset-y-0 left-0 z-30 flex flex-col border-r border-black/[0.06] bg-white w-[88vw] max-w-[320px] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:pt-0 md:pb-0",
        "transition-[transform,width,box-shadow] duration-300 ease-drawer md:duration-200 md:ease-out-strong motion-reduce:transition-none",
        sidebarCollapsed ? "md:w-[80px]" : "md:w-[272px]",
        isMobileMenuOpen ? "translate-x-0 shadow-2xl md:shadow-none" : "-translate-x-full md:translate-x-0"
      )} aria-label="Navigation principale">
        <div className={cn("flex h-[72px] items-center px-5 md:h-[84px]", sidebarCollapsed ? "justify-center px-0" : "justify-between")}>
          {!sidebarCollapsed && (
            <Link to="/dashboard/analytics" className="flex items-center gap-3">
              <img src="/logo.png" alt="Lagoona" className="h-[42px] w-[140px] object-contain object-left" />
            </Link>
          )}
          <button
            type="button"
            aria-label={sidebarCollapsed ? "Déplier le menu" : "Replier le menu"}
            className={cn(pressable, "hidden h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 md:flex")}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
          <button
            type="button"
            aria-label="Fermer le menu"
            className={cn(pressable, "flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 md:hidden")}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={cn("mb-3", sidebarCollapsed ? "flex justify-center" : "px-4")}>
          <Popover open={isSitePickerOpen} onOpenChange={handleSitePickerOpenChange}>
            <PopoverTrigger asChild>
              {sidebarCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Site actuel : ${activeSite?.name || "aucun"}`}
                      className={cn(pressable, surface, "flex h-11 w-11 items-center justify-center rounded-xl outline-none hover:bg-gray-50")}
                    >
                      {siteLogo("sm")}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className={tooltipClassName}>
                    Site : {activeSite?.name || "Choisir…"}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <button
                  type="button"
                  className={cn(pressable, surface, "group flex w-full items-center gap-3 rounded-xl p-2 pr-3 text-left outline-none hover:bg-gray-50")}
                >
                  {siteLogo("md")}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium leading-tight text-gray-500">
                      {activeSite ? getSiteBrand(activeSite.name).brand : "Site actuel"}
                    </p>
                    <p className="truncate text-sm font-semibold leading-tight text-gray-900">
                      {activeSite ? getSiteBrand(activeSite.name).label : "Choisir…"}
                    </p>
                  </div>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600" />
                </button>
              )}
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side={sidebarCollapsed ? "right" : "bottom"}
              collisionPadding={12}
              // Sur téléphone, ne pas ouvrir le clavier à chaque ouverture de la liste.
              onOpenAutoFocus={(event) => {
                if (!window.matchMedia("(pointer: fine)").matches) event.preventDefault();
              }}
              className="w-[min(300px,calc(100vw-2rem))] overflow-hidden rounded-xl border-black/[0.06] p-0 shadow-xl"
            >
              <Command filter={filterSites}>
                <CommandInput
                  value={siteSearch}
                  onValueChange={setSiteSearch}
                  placeholder="Rechercher un site…"
                  className="text-base md:text-sm"
                />
                <CommandList className="max-h-[min(420px,calc(var(--radix-popover-content-available-height)-3rem))] overscroll-contain p-1">
                  <CommandEmpty className="py-6 text-center text-sm text-gray-500">Aucun site trouvé</CommandEmpty>
                  {siteGroups.map((group) => {
                    // Pendant une recherche, toutes les enseignes sont dépliées pour que le filtre voie chaque site.
                    const isSearching = siteSearch.trim() !== "";
                    const isOpen = openSiteBrands.includes(group.brand);
                    const containsActiveSite = group.sites.some(({ site }) => site.id === activeSiteId);
                    const brandLogo = group.sites.find(({ site }) => site.logoUrl)?.site.logoUrl;

                    const siteItems = group.sites.map(({ site, label }) => {
                      const isActive = activeSiteId === site.id;
                      return (
                        <CommandItem
                          key={site.id}
                          value={site.id}
                          keywords={[site.name, group.brand]}
                          onSelect={() => {
                            handleSelectSite(site);
                            setIsSitePickerOpen(false);
                          }}
                          className="cursor-pointer gap-2 rounded-lg px-2 py-1.5 data-[selected=true]:bg-gray-100"
                        >
                          <span className={cn("flex-1 truncate text-sm", isActive ? "font-semibold text-blue-700" : "font-medium text-gray-700")}>{label}</span>
                          {isActive && <Check className="h-4 w-4 shrink-0 text-blue-600" />}
                        </CommandItem>
                      );
                    });

                    // cmdk trie les résultats d'une recherche en déplaçant les éléments : ils doivent être dans un CommandGroup.
                    if (isSearching) {
                      return (
                        <CommandGroup
                          key={group.brand}
                          heading={group.brand}
                          className="p-0 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-gray-500"
                        >
                          {siteItems}
                        </CommandGroup>
                      );
                    }

                    return (
                      <div key={group.brand}>
                        <CommandItem
                          value={`enseigne:${group.brand}`}
                          keywords={[group.brand]}
                          aria-expanded={isOpen}
                          onSelect={() => toggleSiteBrand(group.brand)}
                          className="cursor-pointer gap-3 rounded-lg p-2 data-[selected=true]:bg-gray-100"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-50 ring-1 ring-black/5">
                            {brandLogo ? <img src={brandLogo} alt="" className="h-full w-full object-cover" /> : <Store className="h-3.5 w-3.5 text-gray-400" />}
                          </div>
                          <span className="flex-1 truncate text-sm font-semibold text-gray-900">{group.brand}</span>
                          {containsActiveSite && !isOpen && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" aria-label="Contient le site actuel" />}
                          <span className="shrink-0 text-xs tabular-nums text-gray-400">{group.sites.length}</span>
                          <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150 ease-out-strong", isOpen && "rotate-90")} />
                        </CommandItem>
                        {isOpen && (
                          <div className="mb-1 ml-[22px] border-l border-black/[0.06] pl-1.5">
                            {siteItems}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {canCreateClaim && (
          <div className={cn("mb-3", sidebarCollapsed ? "flex justify-center" : "px-4")}>
            {sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Nouveau dossier QualiRépar"
                    onClick={() => setIsQualiReparDialogOpen(true)}
                    className={cn(pressable, primaryButton, "flex h-11 w-11 items-center justify-center rounded-xl")}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className={tooltipClassName}>
                  Nouveau dossier QualiRépar
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={() => setIsQualiReparDialogOpen(true)}
                className={cn(pressable, primaryButton, "flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold")}
              >
                <Plus className="h-4 w-4" /> Nouveau dossier
              </button>
            )}
          </div>
        )}

        <Separator className="mx-4 mb-2 w-auto bg-black/[0.06]" />

        <nav className="custom-scrollbar flex-1 space-y-3 overflow-y-auto px-3 pb-6 pt-2">
          {navGroups.map((group) => {
            const visibleItems = group.items.filter(item => item.action !== "create-claim" && !item.hidden && userRole && item.roles.includes(userRole));
            if (visibleItems.length === 0) return null;

            if (sidebarCollapsed) {
              return (
                <div key={group.title} className="flex flex-col items-center space-y-1 border-b border-black/[0.06] pb-3 last:border-0">
                  {visibleItems.map((item) => {
                    const isActive = isNavItemActive(item, location.pathname);

                    return (
                      <Tooltip key={item.label}>
                        <TooltipTrigger asChild>
                          <Link
                            to={item.href!}
                            {...preloadLinkProps(item.href)}
                            aria-label={item.label}
                            aria-current={isActive ? "page" : undefined}
                            className={cn(
                              pressable,
                              "flex h-10 w-10 items-center justify-center rounded-lg outline-none",
                              isActive ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                            )}
                          >
                            <item.icon className="h-[18px] w-[18px]" />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" className={tooltipClassName}>
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            }

            const isOpen = openGroups[group.title];

            return (
              <div key={group.title}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleGroup(group.title)}
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-700"
                >
                  <span>{group.title}</span>
                  <ChevronRight className={cn("h-3 w-3 transition-transform duration-200 ease-out-strong", isOpen && "rotate-90")} />
                </button>

                {isOpen && (
                  <div className="space-y-0.5 animate-in fade-in-0 slide-in-from-top-1 duration-150 ease-out-strong motion-reduce:animate-none">
                    {visibleItems.map((item) => {
                      const isActive = isNavItemActive(item, location.pathname);

                      return (
                        <Link
                          key={item.label}
                          to={item.href!}
                          {...preloadLinkProps(item.href)}
                          aria-current={isActive ? "page" : undefined}
                          className={cn(
                            "group/item flex items-center gap-3 rounded-lg px-3 py-3 text-[15px] transition-[color,background-color,transform] duration-150 ease-out active:scale-[0.98] md:py-2 md:text-[13px]",
                            isActive ? "bg-blue-50 font-semibold text-blue-700" : "font-medium text-gray-600 hover:bg-gray-100/80 hover:text-gray-900"
                          )}
                        >
                          <item.icon className={cn("h-4 w-4 shrink-0 transition-colors", isActive ? "text-blue-600" : "text-gray-400 group-hover/item:text-gray-600")} />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="mt-auto shrink-0 space-y-3 border-t border-black/[0.06] p-3">
          {sidebarCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={connectBluetooth}
                  aria-label={`Imprimante : ${printerStatus}`}
                  className={cn(
                    pressable,
                    "relative mx-auto flex h-10 w-10 items-center justify-center rounded-lg outline-none",
                    isBluetoothConnected ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500 hover:bg-red-100/70"
                  )}
                >
                  {printerIcon}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className={tooltipClassName}>
                Imprimante : {printerStatus}
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={connectBluetooth}
              className={cn(pressable, "flex w-full items-center gap-3 rounded-xl p-2 text-left outline-none hover:bg-gray-50")}
            >
              <div className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors",
                isBluetoothConnected ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
              )}>
                {printerIcon}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold leading-tight text-gray-900">Imprimante</p>
                <p className={cn(
                  "mt-0.5 flex items-center gap-1.5 text-xs font-medium leading-tight",
                  isBluetoothConnected ? "text-emerald-600" : isConnecting ? "text-gray-500" : "text-red-500"
                )}>
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
                  {isBluetoothConnected || isConnecting ? printerStatus : "Déconnectée · toucher pour connecter"}
                </p>
              </div>
            </button>
          )}

          <div className={cn("flex items-center gap-3", sidebarCollapsed ? "justify-center" : "px-2 pb-1")}>
            {sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" onClick={handleLogout} aria-label="Déconnexion" className={cn(pressable, "rounded-full outline-none")}>
                    <Avatar className="h-9 w-9 shrink-0 ring-1 ring-black/5">
                      <AvatarFallback className="bg-blue-600 text-xs font-semibold text-white">{initials}</AvatarFallback>
                    </Avatar>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className={tooltipClassName}>
                  Déconnexion ({userEmail?.split('@')[0]})
                </TooltipContent>
              </Tooltip>
            ) : (
              <>
                <Avatar className="h-9 w-9 shrink-0 ring-1 ring-black/5">
                  <AvatarFallback className="bg-blue-600 text-xs font-semibold text-white">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-semibold text-gray-900">{userEmail?.split('@')[0]}</p>
                    {userRole && <Badge className="bg-gray-100 px-1.5 py-0 text-[10px] font-medium capitalize text-gray-500">{userRole}</Badge>}
                  </div>
                  <button type="button" onClick={handleLogout} className="mt-0.5 flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-red-600">
                    <LogOut className="h-3 w-3 shrink-0" /> Déconnexion
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
        <header className="md:hidden flex min-h-14 items-center gap-2 pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] bg-white/90 backdrop-blur-md border-b border-black/[0.06] shrink-0 pt-[env(safe-area-inset-top)]">
          <button
            type="button"
            aria-label="Ouvrir le menu"
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(true)}
            className={cn(pressable, "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100")}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold leading-tight text-gray-900">{currentPageTitle}</p>
            <p className="truncate text-xs font-medium leading-tight text-gray-500">{activeSite?.name || "Aucun site sélectionné"}</p>
          </div>
          {canCreateClaim && (
            <button
              type="button"
              onClick={() => setIsQualiReparDialogOpen(true)}
              className={cn(pressable, primaryButton, "flex h-10 shrink-0 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold")}
            >
              <Plus className="h-4 w-4" /> Dossier
            </button>
          )}
        </header>
        <main className="flex-1 overflow-x-hidden overflow-y-auto relative w-full bg-[#f8f9fa] custom-scrollbar">
          <Outlet />

          {userRole && isQualiReparDialogOpen && (
            <Suspense fallback={null}>
              <QualiReparDialog
                isOpen={isQualiReparDialogOpen}
                onClose={() => setIsQualiReparDialogOpen(false)}
                booking={null}
              />
            </Suspense>
          )}
        </main>
      </div>
    </div>
  );
}

function Badge({ children, className }: { children: React.ReactNode, className?: string }) {
  return <span className={cn("px-2 py-0.5 rounded-full text-xs font-bold", className)}>{children}</span>;
}
